/**
 * AdUnit - Google AdSense ad placement component
 * 
 * Usage:
 *   <AdUnit slot="1234567890" format="auto" />
 *   <AdUnit slot="1234567890" format="rectangle" /> // 300x250
 *   <AdUnit slot="1234567890" format="leaderboard" /> // 728x90
 *   <AdUnit slot="1234567890" format="banner" /> // 320x100
 */
import { useEffect, useRef } from 'react';

const AD_CLIENT = 'ca-pub-9517445443217028';

// Predefined ad formats
const AD_FORMATS = {
  auto: { width: '100%', height: 'auto' },
  rectangle: { width: '300px', height: '250px' },
  leaderboard: { width: '728px', height: '90px' },
  banner: { width: '320px', height: '100px' },
  skyscraper: { width: '160px', height: '600px' },
  largeRectangle: { width: '336px', height: '280px' },
  responsive: { width: '100%', height: 'auto' },
};

export const AdUnit = ({ 
  slot, 
  format = 'auto',
  className = '',
  style = {},
  testMode = false 
}) => {
  const adRef = useRef(null);
  const isLoaded = useRef(false);

  useEffect(() => {
    // Don't load ads in development/test mode unless explicitly enabled
    if (process.env.NODE_ENV === 'development' && !testMode) {
      return;
    }

    // Prevent double-loading
    if (isLoaded.current) return;

    try {
      // Push ad to AdSense
      if (window.adsbygoogle && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isLoaded.current = true;
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, [testMode]);

  // If no slot is provided, don't render anything
  if (!slot) {
    return null;
  }

  const formatStyle = AD_FORMATS[format] || AD_FORMATS.auto;

  // Show placeholder in development only if testMode is enabled
  if (process.env.NODE_ENV === 'development' && !testMode) {
    return null; // Don't show placeholder in dev mode anymore
  }

  return (
    <div className={`ad-container ${className}`} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{
          display: 'block',
          ...formatStyle,
        }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format === 'auto' || format === 'responsive' ? 'auto' : undefined}
        data-full-width-responsive={format === 'responsive' ? 'true' : undefined}
      />
    </div>
  );
};

/**
 * In-feed ad for content lists (schedule, news, VODs)
 */
export const InFeedAd = ({ slot, className = '' }) => {
  const adRef = useRef(null);
  const isLoaded = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return;
    if (isLoaded.current) return;

    try {
      if (window.adsbygoogle && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isLoaded.current = true;
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, []);

  // If no slot is provided, don't render anything
  if (!slot) {
    return null;
  }

  // Don't show in development
  if (process.env.NODE_ENV === 'development') {
    return null;
  }

  return (
    <div className={`infeed-ad-container ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slot}
        data-ad-format="fluid"
        data-ad-layout-key="-6t+ed+2i-1n-4w"
      />
    </div>
  );
};

/**
 * Article/content ad for within article content
 */
export const InArticleAd = ({ slot, className = '' }) => {
  const adRef = useRef(null);
  const isLoaded = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') return;
    if (isLoaded.current) return;

    try {
      if (window.adsbygoogle && adRef.current) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        isLoaded.current = true;
      }
    } catch (err) {
      console.error('AdSense error:', err);
    }
  }, []);

  // If no slot is provided, don't render anything
  if (!slot) {
    return null;
  }

  // Don't show in development
  if (process.env.NODE_ENV === 'development') {
    return null;
  }

  return (
    <div className={`inarticle-ad-container my-4 ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', textAlign: 'center' }}
        data-ad-client={AD_CLIENT}
        data-ad-slot={slot}
        data-ad-format="fluid"
        data-ad-layout="in-article"
      />
    </div>
  );
};

export default AdUnit;
